/**
 * SKY UTILS — shared colour / easing / time helpers and the day-night sky
 * palette used across the sky sub-renderers (gradient, sea, clouds, stars,
 * celestial). Pure: no canvas drawing, no game state.
 */

import { computeMockupUiScale } from "../../layout/mockup-ui-scale.js";
import { resolveGridWidthPx } from "../../layout/responsive-board.js";

export function clamp01(v) {
  if (v <= 0) return 0;
  if (v >= 1) return 1;
  return v;
}

export function lerp(a, b, u) {
  return a + (b - a) * u;
}

export function lerpRgb(c0, c1, u) {
  return [
    Math.round(lerp(c0[0], c1[0], u)),
    Math.round(lerp(c0[1], c1[1], u)),
    Math.round(lerp(c0[2], c1[2], u)),
  ];
}

export function rgb(c, alpha) {
  if (alpha == null) return "rgb(" + c[0] + ", " + c[1] + ", " + c[2] + ")";
  return "rgba(" + c[0] + ", " + c[1] + ", " + c[2] + ", " + alpha + ")";
}

export function normalizeTimeOfDay(timeOfDay) {
  var t = Number(timeOfDay);
  if (!isFinite(t)) return 0;
  t = t % 1;
  if (t < 0) t += 1;
  return t;
}

export function timeSec(currentTime) {
  // 0 is a valid hour-clock time (the very start), so accept >= 0; only fall back
  // to wall-clock when no finite time was supplied.
  var ms = currentTime != null && isFinite(currentTime) && currentTime >= 0
    ? currentTime
    : (typeof performance !== "undefined" ? performance.now() : Date.now());
  return ms * 0.001;
}

/**
 * 0..1 "night-ness" for decor that swaps between the day and night mockup looks
 * (clouds, sea-line highlight): 0 through day/dawn, ramps up across dusk, 1 in
 * deep night, ramps back down before dawn.
 */
export function nightAmount(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  if (t < 0.42) return 0;
  if (t < 0.62) return (t - 0.42) / 0.2;
  if (t <= 0.9) return 1;
  return Math.max(0, 1 - (t - 0.9) / 0.1);
}

/** Interpolate a colour field across a keyframe table at normalized time t. */
export function sampleKeyframes(keyframes, t, field) {
  t = normalizeTimeOfDay(t);
  for (var i = 0; i < keyframes.length - 1; i++) {
    var a = keyframes[i];
    var b = keyframes[i + 1];
    if (t >= a.t && t <= b.t) {
      var span = b.t - a.t;
      var u = span > 0 ? (t - a.t) / span : 0;
      return lerpRgb(a[field], b[field], u);
    }
  }
  return keyframes[keyframes.length - 1][field].slice();
}

/** Resolve the mockup UI scale from grid width (stable when side columns expand). */
export function mockupUiScaleFromOpts(opts, boardBounds) {
  var layout = opts && opts.layout;
  var uiW = layout ? resolveGridWidthPx(layout) : boardBounds.width;
  if (!(uiW > 0)) uiW = boardBounds.width;
  return computeMockupUiScale(uiW).scaleUi;
}

// Cartoon sky palettes (top / mid / bottom of the sky band, plus the pale horizon haze),
// pushed to saturated toon tones: a deep cyan-blue day, a lavender-and-peach dawn, a
// violet-pink-gold sunset and a deep navy night. In-between frames (morning, golden hour,
// dusk, pre-dawn) keep the blends clean instead of passing through grey. The sky is drawn as
// flat bands sampled from this ramp (render/sky/sky-gradient.js) by both renderers.
export var SKY_KEYFRAMES = [
  { t: 0, top: [86, 132, 214], mid: [196, 160, 220], bottom: [255, 204, 166], haze: [255, 232, 204] },
  { t: 0.12, top: [52, 142, 226], mid: [128, 186, 242], bottom: [214, 236, 250], haze: [240, 250, 255] },
  { t: 0.25, top: [38, 150, 230], mid: [86, 192, 243], bottom: [178, 234, 255], haze: [216, 246, 255] },
  { t: 0.38, top: [42, 128, 214], mid: [168, 164, 232], bottom: [255, 212, 150], haze: [255, 238, 194] },
  { t: 0.5, top: [72, 70, 158], mid: [226, 104, 140], bottom: [255, 182, 96], haze: [255, 216, 142] },
  { t: 0.62, top: [30, 36, 100], mid: [80, 62, 136], bottom: [176, 96, 128], haze: [204, 124, 140] },
  { t: 0.75, top: [14, 20, 64], mid: [30, 42, 108], bottom: [56, 76, 154], haze: [80, 102, 182] },
  { t: 0.88, top: [22, 34, 90], mid: [66, 64, 140], bottom: [170, 116, 160], haze: [210, 152, 172] },
  { t: 1, top: [86, 132, 214], mid: [196, 160, 220], bottom: [255, 204, 166], haze: [255, 232, 204] },
];
